import uniq from 'lodash/uniq';

import { ARCHIVED, MODIFIABLE_ORDER_STATES, READY } from '../constants/states-app';
import { GetOrdersOfTotemQuery, OrderFragmentFragment as Order } from 'data/__generated__';

function round(number: number, numberOfDecimals: number = 0): number {
    const multiplier = Math.pow(10, numberOfDecimals);
    return Math.round(number * multiplier) / multiplier;
}

export function roundPrice(number: number): number {
    return round(number, 2);
}

function getCategoriesInOrderIds(
    order: NonNullable<GetOrdersOfTotemQuery['ordersOfTotem']['recentOrders']>[number] | null,
): string[] {
    if (!order) {
        return [];
    }
    return uniq(
        order?.products?.map(({ categoryId }) => categoryId).filter((id): id is string => typeof id === 'string') ?? [],
    );
}

function getCategoryInOrderInfo(
    order: NonNullable<GetOrdersOfTotemQuery['ordersOfTotem']['recentOrders']>[number] | null,
    categoryId: string,
):
    | {
          _id: string;
          quantity: number;
          portion: number;
          price: number;
      }
    | undefined {
    const productsFromCategory = order?.products.filter((product) => product.categoryId === categoryId);
    if (!productsFromCategory?.length) {
        return undefined;
    }
    let quantity = 0;
    let portion = 0;
    let price = 0;

    productsFromCategory.forEach((product) => {
        quantity += product.quantity ?? 0;
        portion += product.portion ?? 0;
        price += (product.unitPrice ?? 0) * (product.quantity ?? 0);
    });

    return {
        _id: categoryId,
        quantity,
        portion,
        price,
    };
}

export function getCategoriesInOrderInfo(
    order: NonNullable<GetOrdersOfTotemQuery['ordersOfTotem']['recentOrders']>[number] | null,
): Array<{
    _id: string;
    quantity: number;
    portion: number;
    price: number;
}> {
    const categoryIds = getCategoriesInOrderIds(order);
    return categoryIds.reduce(
        (acc: Array<{ _id: string; quantity: number; portion: number; price: number }>, categoryId) => {
            const categoryObject = getCategoryInOrderInfo(order, categoryId);
            if (categoryObject) {
                acc.push(categoryObject);
            }
            return acc;
        },
        [],
    );
}

export function getRecurringOrdersForDay(
    ordersOfTotem: NonNullable<GetOrdersOfTotemQuery['ordersOfTotem']> | null,
    day: string | undefined,
): {
    orderPrevious?: Order;
    orderReady?: Order;
    modifiableOrder?: Order;
} {
    const recentOrders = ordersOfTotem?.recentOrders ?? [];
    return (recentOrders as Order[]).reduce(
        (acc, order) => {
            if (!order) return acc;
            const { state, isPunctual, weekday } = order;
            if (!isPunctual && weekday === day) {
                if (state === ARCHIVED) {
                    acc.orderPrevious = order;
                } else if (state === READY) {
                    acc.orderReady = order;
                } else if (state && MODIFIABLE_ORDER_STATES.includes(state)) {
                    acc.modifiableOrder = order;
                }
            }
            return acc;
        },
        {} as {
            orderPrevious?: Order;
            orderReady?: Order;
            modifiableOrder?: Order;
        },
    );
}
